import React, { useState, useMemo, useCallback } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { PageHeader, Spinner, Empty } from "@/components/Atoms";
import { fmtRel } from "@/lib/format";
import {
    Download, Filter, ScrollText, ShieldCheck, Settings2, RefreshCw, Search, X,
} from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

// Cloud-Anzeige: Domain bevorzugt (Operator-Wunsch: keine nackten Cloud-IDs).
const cloudLabel = (c) => c?.primary_domain || c?.fallback_hostname || c?.name || c?.id || "—";

const LEVELS = ["", "DEBUG", "INFO", "WARNING", "ERROR", "FATAL"];
const RANGES = [
    { key: "1h", label: "1h", ms: 3600e3 },
    { key: "24h", label: "24h", ms: 86400e3 },
    { key: "7d", label: "7d", ms: 7 * 86400e3 },
    { key: "14d", label: "14d", ms: 14 * 86400e3 },
    { key: "all", label: "All", ms: 0 },
];

function levelClass(l) {
    switch ((l || "").toUpperCase()) {
        case "FATAL": return "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40";
        case "ERROR": return "bg-red-500/15 text-red-300 border-red-500/40";
        case "WARNING": return "bg-amber-500/15 text-amber-300 border-amber-500/40";
        case "INFO": return "bg-sky-500/15 text-sky-300 border-sky-500/40";
        case "DEBUG": return "bg-zinc-500/15 text-zinc-400 border-zinc-600/40";
        default: return "bg-zinc-500/15 text-zinc-400 border-zinc-600/40";
    }
}

// =========================================================
export default function LogsPage() {
    const [mode, setMode] = useState("logs"); // logs | audit
    const [cloudId, setCloudId] = useState("");
    const [source, setSource] = useState("");
    const [role, setRole] = useState("");
    const [level, setLevel] = useState("");
    const [app, setApp] = useState("");
    const [user, setUser] = useState("");
    const [q, setQ] = useState("");
    const [range, setRange] = useState("24h");
    const [live, setLive] = useState(false);
    const [limit, setLimit] = useState(200);
    const [showSettings, setShowSettings] = useState(false);
    const [selected, setSelected] = useState(new Set());

    const { data: status } = useSWR("/api/admin/logs/status", fetcher);
    const { data: clouds } = useSWR("/api/admin/clouds", fetcher);
    const { data: facets } = useSWR(
        `/api/admin/logs/facets${cloudId ? `?cloud_id=${cloudId}` : ""}`, fetcher);

    // Domain-sortierte Cloud-Liste + id→Label-Map (für Dropdown & Tabelle).
    const sortedClouds = useMemo(
        () => [...(clouds || [])].sort((a, b) => cloudLabel(a).localeCompare(cloudLabel(b))),
        [clouds]);
    const cloudMap = useMemo(() => {
        const m = {};
        (clouds || []).forEach((c) => { m[c.id] = cloudLabel(c); });
        return m;
    }, [clouds]);

    const since = useMemo(() => {
        const r = RANGES.find((x) => x.key === range);
        if (!r || !r.ms) return "";
        return new Date(Date.now() - r.ms).toISOString();
    }, [range]);

    const qs = useMemo(() => {
        const p = new URLSearchParams();
        if (cloudId) p.set("cloud_id", cloudId);
        if (mode === "logs") {
            if (source) p.set("source", source);
            if (role) p.set("role", role);
            if (level) p.set("level", level);
            if (app) p.set("app", app);
        }
        if (user) p.set("user", user);
        if (q) p.set("q", q);
        if (since) p.set("since", since);
        p.set("limit", String(limit));
        return p.toString();
    }, [cloudId, source, role, level, app, user, q, since, limit, mode]);

    const endpoint = mode === "logs" ? "/api/admin/logs" : "/api/admin/logs/audit";
    const { data, isLoading, mutate } = useSWR(
        status?.enabled ? `${endpoint}?${qs}` : null, fetcher,
        { refreshInterval: live ? 5000 : 0 });

    const items = useMemo(() => data?.items || [], [data]);
    const disabled = status && !status.enabled;

    const toggleSelect = (i) => {
        const next = new Set(selected);
        if (next.has(i)) next.delete(i); else next.add(i);
        setSelected(next);
    };
    const toggleSelectAll = () => {
        if (selected.size === items.length) { setSelected(new Set()); }
        else { setSelected(new Set(items.map((_, i) => i))); }
    };

    const handleExport = useCallback(() => {
        const toExport = items.filter((_, i) => selected.has(i));
        if (!toExport.length) return;
        const lines = [];
        for (const e of toExport) {
            const ts = e.ts || "";
            const lvl = e.level || "";
            const dom = cloudMap[e.cloud_id] || (e.cloud_id || "").slice(0, 8) || "";
            const src = [e.source, e.role, e.vmid ? `#${e.vmid}` : ""].filter(Boolean).join(" ");
            const app = e.app || "";
            const usr = e.user || "";
            const req = e.request_id || "";
            const url = e.url || "";
            const msg = e.message || "";
            lines.push(`[${ts}] ${lvl} | ${dom} | ${src}`);
            if (url) lines.push(`URL: ${url}`);
            lines.push(msg);
            if (req || usr || app) {
                const meta = [`Req-ID: ${req}`, usr ? `User: ${usr}` : "", app ? `App: ${app}` : ""].filter(Boolean).join(" | ");
                lines.push(meta);
            }
            lines.push("---");
        }
        const blob = new Blob([lines.join("\n")], { type: "text/plain" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `cm-logs-${new Date().toISOString().slice(0, 10)}.txt`;
        a.click();
        URL.revokeObjectURL(a.href);
    }, [items, selected, cloudMap]);

    const resetFilters = () => {
        setCloudId(""); setSource(""); setRole(""); setLevel("");
        setApp(""); setUser(""); setQ(""); setRange("24h"); setLimit(200);
    };

    return (
        <div data-testid="logs-page">
            <PageHeader
                testid="logs-header"
                title="Logs"
                subtitle="Zentrale Nextcloud- & Infra-Logs aus allen Clouds (ClickHouse) — filterbar in Echtzeit."
                action={
                    <div className="flex items-center gap-2">
                        {items.length > 0 && selected.size > 0 && (
                            <button
                                data-testid="logs-export-btn"
                                onClick={handleExport}
                                className="cm-btn-primary flex items-center gap-2 text-xs"
                            >
                                <Download size={14} /> {selected.size} export
                            </button>
                        )}
                        <button
                            data-testid="logs-settings-btn"
                            onClick={() => setShowSettings(true)}
                            className="cm-btn-secondary flex items-center gap-2 text-xs"
                        >
                            <Settings2 size={14} /> Collection
                        </button>
                        <button
                            data-testid="logs-live-toggle"
                            onClick={() => setLive((v) => !v)}
                            className={`cm-btn-secondary flex items-center gap-2 text-xs ${live ? "!border-[#CCFF00] !text-sky-300" : ""}`}
                        >
                            <RefreshCw size={14} className={live ? "animate-spin" : ""} />
                            {live ? "Live on" : "Live off"}
                        </button>
                    </div>
                }
            />

            {/* Mode toggle */}
            <div className="px-4 sm:px-6 pt-4 flex gap-px bg-zinc-800 w-fit">
                <button
                    data-testid="logs-mode-operational"
                    onClick={() => setMode("logs")}
                    className={`px-4 py-2 text-xs font-mono uppercase tracking-widest flex items-center gap-2 ${mode === "logs" ? "bg-[#CCFF00] text-[#05070A]" : "bg-zinc-950 text-zinc-400 hover:text-zinc-100"}`}
                >
                    <ScrollText size={12} /> Operativ
                </button>
                <button
                    data-testid="logs-mode-audit"
                    onClick={() => setMode("audit")}
                    className={`px-4 py-2 text-xs font-mono uppercase tracking-widest flex items-center gap-2 ${mode === "audit" ? "bg-[#CCFF00] text-[#05070A]" : "bg-zinc-950 text-zinc-400 hover:text-zinc-100"}`}
                >
                    <ShieldCheck size={12} /> Audit
                </button>
            </div>

            {disabled && (
                <div className="mx-4 sm:mx-6 mt-4 cm-card p-4 border border-amber-500/40 bg-amber-500/5" data-testid="logs-disabled-banner">
                    <div className="text-sm text-amber-300 font-medium">Log store not configured</div>
                    <div className="text-xs text-zinc-400 mt-1">
                        ClickHouse is not reachable (<code>CLICKHOUSE_URL</code> not set).
                        Central logging is disabled; CM keeps running unaffected.
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2">
                <Filter size={14} className="text-zinc-500" />
                <select data-testid="logs-filter-cloud" className="cm-input max-w-[260px]"
                    value={cloudId} onChange={(e) => setCloudId(e.target.value)}>
                    <option value="">All clouds</option>
                    {sortedClouds.map((c) => (
                        <option key={c.id} value={c.id}>{cloudLabel(c)}</option>
                    ))}
                </select>

                {mode === "logs" && (
                    <>
                        <select data-testid="logs-filter-source" className="cm-input max-w-[150px]"
                            value={source} onChange={(e) => setSource(e.target.value)}>
                            <option value="">All sources</option>
                            {(facets?.sources || []).map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select data-testid="logs-filter-role" className="cm-input max-w-[130px]"
                            value={role} onChange={(e) => setRole(e.target.value)}>
                            <option value="">All roles</option>
                            {(facets?.roles || []).map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <select data-testid="logs-filter-level" className="cm-input max-w-[130px]"
                            value={level} onChange={(e) => setLevel(e.target.value)}>
                            {LEVELS.map((l) => <option key={l} value={l}>{l || "All levels"}</option>)}
                        </select>
                        <input data-testid="logs-filter-app" className="cm-input max-w-[140px] font-mono"
                            placeholder="App" value={app} onChange={(e) => setApp(e.target.value)} />
                    </>
                )}
                <input data-testid="logs-filter-user" className="cm-input max-w-[140px] font-mono"
                    placeholder="User" value={user} onChange={(e) => setUser(e.target.value)} />

                <div className="relative">
                    <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input data-testid="logs-filter-search" className="cm-input pl-7 max-w-[220px]"
                        placeholder="Full-text search…" value={q}
                        onChange={(e) => setQ(e.target.value)} />
                </div>

                <div className="flex gap-px bg-zinc-800">
                    {RANGES.map((r) => (
                        <button key={r.key} data-testid={`logs-range-${r.key}`}
                            onClick={() => setRange(r.key)}
                            className={`px-2.5 py-1.5 text-xs font-mono ${range === r.key ? "bg-[#CCFF00] text-[#05070A]" : "bg-zinc-950 text-zinc-400 hover:text-zinc-100"}`}>
                            {r.label}
                        </button>
                    ))}
                </div>

                <button data-testid="logs-reset-btn" onClick={resetFilters}
                    className="cm-btn-ghost text-xs flex items-center gap-1">
                    <X size={12} /> Reset
                </button>

                <div className="ml-auto text-xs font-mono text-zinc-500" data-testid="logs-count">
                    {items.length} entries
                </div>
            </div>

            {/* Table */}
            <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                {!status ? (
                    <Spinner />
                ) : disabled ? null : isLoading ? (
                    <Spinner label="Loading logs…" />
                ) : !items.length ? (
                    <Empty title="No log entries" hint="Adjust the filters or extend the time range." testid="logs-empty" />
                ) : (
                    <div className="cm-card overflow-hidden">
                        <div className="cm-table-wrap">
                            <table className="cm-table">
                                <thead>
                                    <tr>
                                        <th className="cm-th w-8">
                                            <input type="checkbox"
                                                data-testid="logs-select-all"
                                                checked={items.length > 0 && selected.size === items.length}
                                                onChange={toggleSelectAll}
                                                className="w-3.5 h-3.5 accent-[#CCFF00]" />
                                        </th>
                                        <th className="cm-th">Time</th>
                                        {mode === "logs" && <th className="cm-th">Level</th>}
                                        <th className="cm-th">Cloud</th>
                                        {mode === "logs" && <th className="cm-th">Source/Role</th>}
                                        <th className="cm-th">App</th>
                                        <th className="cm-th">User</th>
                                        {mode === "audit" && <th className="cm-th">Action</th>}
                                        <th className="cm-th">Req-ID</th>
                                        <th className="cm-th">Message</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((e, i) => (
                                        <tr key={i} data-testid={`logs-row-${i}`}>
                                            <td className="cm-td">
                                                <input type="checkbox"
                                                    data-testid={`logs-select-${i}`}
                                                    checked={selected.has(i)}
                                                    onChange={() => toggleSelect(i)}
                                                    className="w-3.5 h-3.5 accent-[#CCFF00]" />
                                            </td>
                                            <td className="cm-td font-mono text-xs text-zinc-500 whitespace-nowrap" title={e.ts}>
                                                {fmtRel(e.ts?.replace(" ", "T") + "Z")}
                                            </td>
                                            {mode === "logs" && (
                                                <td className="cm-td">
                                                    <span className={`px-1.5 py-0.5 rounded border text-[11px] font-mono ${levelClass(e.level)}`}>
                                                        {e.level}
                                                    </span>
                                                </td>
                                            )}
                                            <td className="cm-td font-mono text-xs text-zinc-400 max-w-[220px] truncate" title={e.cloud_id}>
                                                {cloudMap[e.cloud_id] || (e.cloud_id || "").slice(0, 8) || "—"}
                                            </td>
                                            {mode === "logs" && (
                                                <td className="cm-td font-mono text-xs text-zinc-400">
                                                    {e.source}<span className="text-zinc-600"> · </span>{e.role}
                                                    {e.vmid ? <span className="text-zinc-600"> #{e.vmid}</span> : null}
                                                </td>
                                            )}
                                            <td className="cm-td font-mono text-xs text-zinc-300">{e.app || "—"}</td>
                                            <td className="cm-td font-mono text-xs text-zinc-400">{e.user || "—"}</td>
                                            {mode === "audit" && (
                                                <td className="cm-td font-mono text-xs text-sky-300">{e.action || "—"}</td>
                                            )}
                                            <td className="cm-td font-mono text-[11px] text-zinc-500 max-w-[180px] truncate" title={e.request_id}>
                                                {e.request_id || "—"}
                                            </td>
                                            <td className="cm-td text-xs text-zinc-200 max-w-xl break-words">{e.message}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {items.length >= limit && (
                    <div className="mt-3 text-center">
                        <button data-testid="logs-load-more" onClick={() => setLimit((l) => l + 200)}
                            className="cm-btn-secondary text-xs">Load more (+200)</button>
                    </div>
                )}
            </div>

            {showSettings && (
                <LogSettingsDrawer
                    clouds={clouds || []}
                    selectedCloud={cloudId}
                    onClose={() => { setShowSettings(false); mutate(); }}
                />
            )}
        </div>
    );
}

// =========================================================
function LogSettingsDrawer({ clouds, selectedCloud, onClose }) {
    const [target, setTarget] = useState(selectedCloud || "_global");
    const { data: global } = useSWR("/api/admin/logs/settings", fetcher);
    const { data: cur, mutate } = useSWR(
        target && target !== "_global" ? `/api/admin/logs/settings/${target}` : null, fetcher);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    const eff = target === "_global" ? global?.global : cur;
    const [level, setLevel] = useState(null);
    const [enabled, setEnabled] = useState(null);
    React.useEffect(() => {
        if (eff) { setLevel(eff.min_level); setEnabled(eff.enabled); }
    }, [eff]);

    const save = async () => {
        setSaving(true); setMsg(null);
        try {
            await api.put(`/api/admin/logs/settings/${target}`, { min_level: level, enabled });
            setMsg("Saved.");
            mutate();
        } catch (e) {
            setMsg(e?.response?.data?.detail || "Error saving");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="logs-settings-drawer">
            <button className="absolute inset-0 bg-black/60" onClick={onClose} aria-label="Close" />
            <div className="relative w-full max-w-md h-full bg-[#0B0E14] border-l border-zinc-800 p-6 overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <div className="font-display text-lg text-zinc-50">Log Collection</div>
                    <button onClick={onClose} className="cm-btn-ghost p-1" data-testid="logs-settings-close"><X size={16} /></button>
                </div>
                <p className="text-xs text-zinc-400 mb-4">
                    Default level is <b>WARNING</b> (keeps volume low across thousands of clouds).
                    Per cloud you can switch up to INFO/DEBUG. Gating already happens
                    in the agent — only what is allowed here gets sent at all.
                </p>

                <label className="cm-label">Target</label>
                <select data-testid="logs-settings-target" className="cm-input w-full mt-1 mb-4"
                    value={target} onChange={(e) => setTarget(e.target.value)}>
                    <option value="_global">Fleet-Default (global)</option>
                    {[...clouds].sort((a, b) => cloudLabel(a).localeCompare(cloudLabel(b)))
                        .map((c) => <option key={c.id} value={c.id}>{cloudLabel(c)}</option>)}
                </select>

                <label className="cm-label">Minimum level</label>
                <select data-testid="logs-settings-level" className="cm-input w-full mt-1 mb-4"
                    value={level ?? ""} onChange={(e) => setLevel(e.target.value)}>
                    {["DEBUG", "INFO", "WARNING", "ERROR", "FATAL"].map((l) => <option key={l} value={l}>{l}</option>)}
                </select>

                <label className="flex items-center gap-2 mb-4 cursor-pointer">
                    <input type="checkbox" data-testid="logs-settings-enabled"
                        checked={!!enabled} onChange={(e) => setEnabled(e.target.checked)} />
                    <span className="text-sm text-zinc-200">Collection enabled</span>
                </label>

                {target !== "_global" && cur && !cur.overridden && (
                    <div className="text-xs text-zinc-500 mb-3">Currently using the fleet default (no override).</div>
                )}

                {msg && <div className="text-xs text-sky-300 mb-3" data-testid="logs-settings-msg">{msg}</div>}
                <button data-testid="logs-settings-save" onClick={save} disabled={saving}
                    className="cm-btn-primary w-full text-sm">
                    {saving ? "Saving…" : "Save"}
                </button>
            </div>
        </div>
    );
}
